import { useEffect, useState, useMemo, memo } from "react";
import { useStrategist } from "@/hooks/useStrategist";
import { useApp } from "@/context/AppCtx";
import { usePumpPortalTrades } from "@/hooks/usePumpPortalTrades";
import { deployerStore } from "@/lib/deployerStore";
import { fetchTokenTradeHistory, type OnChainTrade } from "@/lib/solanaTradeHistory";
import { formatSol } from "@/lib/formatUsd";
import { Zap, Wallet, ShieldAlert, ShieldCheck, RefreshCw, Activity, ExternalLink, ChevronRight, ChevronDown } from "lucide-react";
import { formatTimeAgo, formatDuration } from "@/lib/timeUtils";
import { fetchWalletStats, fetchTradeIntelligence, type WalletStats, type TradeStats } from "@/lib/walletIntelligence";
import { useTranslation } from "@/lib/i18n/useTranslation";


export function SignalEvidencePanel({ mint, variant = "compact" }: { mint?: string; variant?: "compact" | "wide" }) {
  const { t } = useTranslation();
  const { caMintInput } = useApp() as any;
  const targetMint = mint || caMintInput;
  const strategist = useStrategist();
  
  // Tabs state - removed curated tab
  
  // 1. Live on-chain data stream for the current token
  const { rows: liveTrades } = usePumpPortalTrades(targetMint);
  const [historicalTrades, setHistoricalTrades] = useState<OnChainTrade[]>([]);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);


  useEffect(() => {
    if (!targetMint) return;
    setIsFetchingHistory(true);
    fetchTokenTradeHistory(targetMint)
      .then(trades => {
        setHistoricalTrades(trades);
      })
      .catch(err => {
        console.error("Failed to fetch trade history:", err);
        setHistoricalTrades([]);
      })
      .finally(() => setIsFetchingHistory(false));
  }, [targetMint]);
  
  // 2. Pull the raw tape from the engine snapshot (Full on-chain history we have)
  const snapshot = strategist?.snapshot;
  const allTokens = snapshot ? [...(snapshot.visibleTokens || [])] : [];
  const tokenData = allTokens.find(t => t.mint === targetMint);
  
  // 3. Fallback to formal signal evidence if snapshot isn't available
  const activeSignal = strategist?.recommendations?.find(r => r.mint === targetMint);
  
  const rawTape = (tokenData && "tape" in tokenData ? tokenData.tape : []) || [];
  const evidenceTrades = activeSignal?.evidenceTrades;
  
  // Live Tape Print - show ALL trades, not just curated
  const [liveTapeState, setLiveTapeState] = useState<any[]>([]);
  
  useEffect(() => {
    const safeLive = liveTrades || [];
    const safeRaw = rawTape || [];
    const safeHist = historicalTrades || [];
    const safeEv = evidenceTrades || [];

    const mappedLive = safeLive.map(lt => ({
      ts: lt.ts,
      isBuy: lt.buy,
      sol: lt.sol,
      user: lt.trader,
      id: lt.id,
      isCurated: false
    })) as any[];

    // Start with ALL live trades (not just curated)
    const merged = [...mappedLive];
    
    // Add raw tape trades (full on-chain history) - these are NOT curated
    safeRaw.forEach((t: any) => {
      if (!merged.some(m => m.ts === t.ts && m.user === t.user)) {
        merged.push({ ...t, isCurated: false });
      }
    });

    // Add historical trades (whale action) - mark as curated for highlighting
    safeHist.forEach(ht => {
      if (ht.sol >= 0.5 && !merged.some(m => m.signature === ht.signature || (m.ts === ht.ts && m.user === ht.user))) {
        merged.push({ ...ht, isCurated: true });
      }
    });

    // Add curated evidence trades from strategy recommenders - mark as curated for highlighting
    safeEv.forEach(et => {
      if (!merged.some(t => t.ts === et.ts && t.user === et.user)) {
        merged.push({ ...et, isCurated: true });
      }
    });

    const sorted = merged.sort((a, b) => b.ts - a.ts);
    
    // Update state, keeping existing items and adding new ones
    setLiveTapeState(prev => {
      const existingIds = new Set(prev.map(t => t.id || `${t.ts}-${t.user}`));
      const newItems = sorted.filter(t => !existingIds.has(t.id || `${t.ts}-${t.user}`));
      if (newItems.length === 0) return prev; // Avoid unnecessary state updates and re-renders
      return [...newItems, ...prev].sort((a, b) => b.ts - a.ts).slice(0, 200); // Keep last 200 items
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTrades.length, rawTape.length, historicalTrades.length, evidenceTrades?.length]);

  const liveTape = liveTapeState;

  const label = tokenData?.symbol || activeSignal?.coin || activeSignal?.label || "Live Tape";
  const confidence = activeSignal?.confidence || 0;
  
  // Deployer Reputation
  const creatorAddress = tokenData?.creator || (activeSignal as any)?.creator;
  const deployerTag = creatorAddress ? deployerStore.getTag(creatorAddress) : null;

  // Group sequential trades from the same wallet
  const groupedTape = useMemo(() => {
    const groups: any[] = [];
    let currentGroup: any = null;
    
    for (const trade of liveTape) {
      if (!trade.user || !currentGroup || currentGroup.user !== trade.user) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = {
          user: trade.user,
          trades: [trade],
          totalSol: trade.sol,
          type: trade.isBuy !== false ? 'buy' : 'sell',
          startTime: trade.ts,
          endTime: trade.ts,
          isCurated: (trade as any).isCurated,
          id: `group-${trade.ts}-${trade.user}`
        };
      } else {
        currentGroup.trades.push(trade);
        currentGroup.totalSol += trade.sol;
        currentGroup.startTime = trade.ts; 
        
        const isTradeBuy = trade.isBuy !== false;
        if ((currentGroup.type === 'buy' && !isTradeBuy) || (currentGroup.type === 'sell' && isTradeBuy)) {
          currentGroup.type = 'mixed';
        }
        currentGroup.isCurated = currentGroup.isCurated || (trade as any).isCurated;
      }
    }
    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [liveTape]);

  // Track wallet occurrences across active live tape for stats
  const walletCounts = useMemo(() => {
    const counts = new Map<string, number>();
    liveTape.forEach((t) => {
      if (t.user) {
        counts.set(t.user, (counts.get(t.user) || 0) + 1);
      }
    });
    return counts;
  }, [liveTape]);

  const liveTapeTable = (
    liveTape.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-10 opacity-40">
        {isFetchingHistory ? (
          <div className="flex items-center gap-2">
            <RefreshCw className="size-3 animate-spin text-emerald-400" />
            <p className="text-[9px] text-[var(--color-fg-dim)] uppercase tracking-widest font-bold">{t("signalEvidence.syncing")}</p>
          </div>
        ) : (
          <p className="text-[9px] text-[var(--color-fg-dim)] uppercase tracking-widest font-bold">{t("signalEvidence.empty")}</p>
        )}
      </div>
    ) : (
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-white/5 bg-black/10">
            <th className="px-3 py-2 text-[9px] font-black uppercase tracking-widest text-white/30 text-center w-12">Avatar</th>
            <th className="px-2 py-2 text-[9px] font-black uppercase tracking-widest text-white/30">Action</th>
            <th className="px-2 py-2 text-[9px] font-black uppercase tracking-widest text-white/30">Wallet</th>
            <th className="px-2 py-2 text-[9px] font-black uppercase tracking-widest text-white/30 text-right">Size</th>
            <th className="px-2 py-2 text-[9px] font-black uppercase tracking-widest text-white/30 text-right">Intel</th>
            <th className="px-3 py-2 text-[9px] font-black uppercase tracking-widest text-white/30 text-right">PnL</th>
          </tr>
        </thead>
        <tbody>
          {groupedTape.slice(0, 80).map((group, i) => {
            const occurrences = group.user ? (walletCounts.get(group.user) || 0) : 0;
            return (
              <EvidenceRowGroup 
                key={group.id || i}
                group={group} 
                mint={targetMint} 
                occurrences={occurrences}
              />
            );
          })}
        </tbody>
      </table>
    )
  );


  return (
    <div className="flex flex-col bg-[var(--color-bg-editor)] border border-white/5 rounded-xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-emerald-500/10 to-transparent border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="size-8 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center border border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.15)]">
            <Zap className="size-4 text-[#00FFA3]" />
          </div>
          <div className="flex flex-col">
            <span className="text-xs font-black uppercase tracking-widest text-white drop-shadow-sm">{t("signalEvidence.title")}</span>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="size-1.5 rounded-full bg-[#00FFA3] animate-pulse shadow-[0_0_8px_rgba(0,255,163,0.8)]" />
              <span className="text-[10px] text-[#00FFA3]/80 font-bold uppercase tracking-wider">
                {isFetchingHistory ? t("signalEvidence.syncing") : label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {deployerTag && (
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded border border-white/5 bg-black/20 ${deployerTag.color}`}>
              {deployerTag.isSerial ? <ShieldAlert className="size-2.5" /> : <ShieldCheck className="size-2.5" />}
              <span className="text-[9px] font-black uppercase tracking-widest">{deployerTag.label}</span>
            </div>
          )}
          <div className="flex flex-col items-end">
            <span className="text-[11px] font-mono font-black text-white">{confidence.toFixed(2)}%</span>
            <span className="text-[8px] text-white/20 font-bold uppercase tracking-tighter">{t("signalEvidence.confidence")}</span>
          </div>
        </div>
      </div>

      {variant === "wide" ? (
        /* Wide Mode - Single live tape table */
        <div className="p-4 bg-black/10">
          <div className="flex flex-col border border-white/5 rounded-xl bg-black/30 overflow-hidden shadow-md">
            <div className="px-3.5 py-2.5 bg-black/40 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="size-3.5 text-emerald-400" />
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400">{t("signalEvidence.liveTape")}</span>
                <span className="text-[8px] text-amber-400/70 font-bold uppercase tracking-wider bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                  {t("signalEvidence.curated")}
                </span>
              </div>
              <span className="text-[9px] font-mono font-bold text-white/40 bg-white/5 px-1.5 py-0.2 rounded">{liveTape.length}</span>
            </div>
            <div className="">
              {liveTapeTable}
            </div>
          </div>
        </div>
      ) : (
        /* Compact Mode - Single live tape table */
        <>
          <div className="flex border-b border-white/5 bg-black/10 px-4 py-1.5 gap-2 shrink-0 items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="size-3.5 text-emerald-400" />
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400">{t("signalEvidence.liveTape")}</span>
              <span className="text-[8px] text-amber-400/70 font-bold uppercase tracking-wider bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                {t("signalEvidence.curated")}
              </span>
            </div>
            <span className="text-[9px] font-mono font-bold text-white/40 bg-white/5 px-1.5 py-0.2 rounded">{liveTape.length}</span>
          </div>
          <div className="flex-1 w-full">
            {liveTapeTable}
          </div>
        </>
      )}

      {/* Description Footer */}
      <div className="px-4 py-2.5 bg-black/20 border-t border-white/5 shrink-0">
        <p className="text-[10px] text-[var(--color-fg-dim)] leading-relaxed italic">
          "{activeSignal?.desc || "Real-time trade tape showing active market pressure."}"
        </p>
      </div>
    </div>
  );
}

const EvidenceRowGroup = memo(function EvidenceRowGroup({ 
  group, 
  mint,
  occurrences,
}: { 
  group: any; 
  mint: string; 
  occurrences: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMultiple = group.trades.length > 1;

  if (!isMultiple) {
    return <EvidenceRow trade={group.trades[0]} mint={mint} occurrences={occurrences} isCurated={group.isCurated} />;
  }

  const isBuy = group.type === 'buy';
  const isMixed = group.type === 'mixed';
  const isWhale = group.totalSol >= 0.5;

  return (
    <>
      <tr 
        onClick={() => setIsExpanded(!isExpanded)}
        className={`border-b border-white/5 hover:bg-white/[0.04] transition-all duration-300 cursor-pointer group ${group.isCurated ? "bg-gradient-to-r from-amber-500/10 to-transparent border-l-[3px] border-l-amber-500 shadow-[inset_0_0_20px_rgba(245,158,11,0.05)]" : ""} ${isWhale && !group.isCurated ? (isBuy ? "bg-gradient-to-r from-[#00FFA3]/5 to-transparent" : "bg-gradient-to-r from-[#FF3358]/5 to-transparent") : ""}`}
      >
        <td colSpan={6} className="px-2 py-1.5 align-top">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronDown className="size-3 text-white/40" /> : <ChevronRight className="size-3 text-white/40" />}
              <span className="text-[10px] font-mono text-[var(--color-fg-dim)]">
                {group.user ? `${group.user.slice(0, 3)}..${group.user.slice(-3)}` : "Unknown"} Group ({group.trades.length} TXs)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[12px] font-mono font-black ${isMixed ? "text-purple-400" : isBuy ? "text-[#00FFA3]" : "text-[#FF3358]"}`}>
                {formatSol(group.totalSol)} SOL
              </span>
              <span className="text-[8px] text-white/30 italic">Total Vol</span>
            </div>
          </div>
        </td>
      </tr>
      {isExpanded && group.trades.map((trade: any, idx: number) => (
        <EvidenceRow 
          key={`${trade.ts}-${trade.id || idx}`} 
          trade={trade} 
          mint={mint} 
          occurrences={occurrences} 
          isCurated={trade.isCurated} 
        />
      ))}
    </>
  );
});

const EvidenceRow = memo(function EvidenceRow({ 
  trade, 
  mint,
  occurrences,
  isCurated,
}: { 
  trade: any; 
  mint: string; 
  occurrences: number;
  isCurated?: boolean;
}) {
  const [stats, setStats] = useState<WalletStats | null>(null);
  const [tradeIntel, setTradeIntel] = useState<TradeStats | null>(null);
  const [loading, setLoading] = useState(true);

  const isBuy = trade.isBuy !== false;
  const isWhale = trade.sol >= 0.5;
  const isCuratedItem = isCurated || trade.isCurated;

  useEffect(() => {
    if (!trade.user) return;
    
    // ONLY fetch wallet stats for whale trades to save 95%+ of Helius RPC credits
    if (!isWhale) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    Promise.all([
      fetchWalletStats(trade.user),
      !trade.isBuy ? fetchTradeIntelligence(trade.user, mint, trade.ts, trade.tokenAmount ? (trade.sol / trade.tokenAmount) : undefined) : Promise.resolve(null)
    ]).then(([wStats, tIntel]) => {
      setStats(wStats);
      setTradeIntel(tIntel);
      setLoading(false);
    });
  }, [trade.user, trade.ts, mint, isWhale]);

  return (
    <tr className={`border-b border-white/5 hover:bg-white/[0.04] transition-all duration-300 group ${isCuratedItem ? "bg-gradient-to-r from-amber-500/10 to-transparent border-l-[3px] border-l-amber-500 shadow-[inset_0_0_20px_rgba(245,158,11,0.05)]" : ""} ${isWhale && !isCuratedItem ? (isBuy ? "bg-gradient-to-r from-[#00FFA3]/5 to-transparent" : "bg-gradient-to-r from-[#FF3358]/5 to-transparent") : ""}`}>
      
      {/* 1. AVATAR & TIME COLUMN */}
      <td className="px-2 py-2 align-top text-center w-12">
        <div className="flex flex-col items-center gap-1.5">
          <div className="relative shrink-0">
            {stats?.pumpProfile?.avatar ? (
              <img src={stats.pumpProfile.avatar} alt="Profile" className="size-8 rounded-lg object-cover bg-white/5 border border-white/10 shadow-md" />
            ) : (
              <div className="size-8 rounded-lg bg-white/5 flex items-center justify-center border border-white/5 shadow-inner">
                <Wallet className="size-4 text-white/30" />
              </div>
            )}
            {stats?.isActive && (
              <div className="absolute -bottom-1 -right-1 size-3 bg-black rounded-full flex items-center justify-center">
                <div className="size-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_5px_rgba(16,185,129,0.8)]" />
              </div>
            )}
          </div>
          <span className="text-[9px] font-mono text-white/50">{formatTimeAgo(trade.ts)}</span>
        </div>
      </td>

      {/* 2. ACTION COLUMN */}
      <td className="px-2 py-2 align-top whitespace-nowrap">
        <div className="flex flex-col gap-1">
          <span className={`text-[11px] font-black uppercase tracking-widest ${isBuy ? "text-[#00FFA3] drop-shadow-[0_0_8px_rgba(0,255,163,0.4)]" : "text-[#FF3358] drop-shadow-[0_0_8px_rgba(255,51,88,0.4)]"}`}>
            {isBuy ? "BUY" : "SELL"}
          </span>
          {isWhale && isBuy && (
            <span className="text-[8px] text-[#00FFA3] font-black uppercase tracking-widest animate-pulse bg-[#00FFA3]/10 px-1 py-0.5 rounded max-w-fit">
              Whale Print
            </span>
          )}
        </div>
      </td>

      {/* 3. WALLET COLUMN */}
      <td className="px-2 py-2 align-top">
        <div className="flex flex-col gap-1">
          <a
            href={trade.user ? `https://solscan.io/account/${trade.user}` : "#"}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] font-bold text-[var(--color-fg-dim)] hover:text-white transition-colors truncate flex items-center gap-1 group/link"
          >
            {stats?.pumpProfile?.username || (trade.user ? `${trade.user.slice(0, 4)}..${trade.user.slice(-4)}` : "Unknown")}
            <ExternalLink className="size-2.5 opacity-0 group-hover/link:opacity-100 transition-opacity text-white/40" />
          </a>
          {occurrences > 1 && (
            <span className="text-[8px] font-black uppercase tracking-tighter bg-amber-500/15 text-amber-400 border border-amber-500/20 px-1 rounded w-fit">
              {occurrences}x Active
            </span>
          )}
        </div>
      </td>

      {/* 4. SIZE COLUMN */}
      <td className="px-2 py-2 text-right align-top">
        <span className={`text-[12px] font-mono font-bold ${isBuy ? "text-[#00FFA3]/80" : "text-[#FF3358]/80"}`}>
          {formatSol(trade.sol)} SOL
        </span>
      </td>

      {/* 5. INTEL COLUMN */}
      <td className="px-2 py-2 text-right align-top">
        {loading ? (
          <div className="h-4 w-12 bg-white/5 animate-pulse rounded ml-auto" />
        ) : stats ? (
          <div className="flex flex-col items-end gap-1">
            <span 
              className={`text-[11px] font-mono font-black ${stats?.isHighValue ? "text-amber-400" : "text-white/60"}`}
              title={stats?.usdValue ? `${stats?.solBalance.toFixed(2)} SOL` : undefined}
            >
              {stats?.usdValue ? `$${stats.usdValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `${stats?.solBalance.toFixed(2)} SOL`}
            </span>
            <div className="flex items-center gap-1 justify-end flex-wrap mt-0.5">
              {stats?.topAssets?.slice(0, 3).map((asset, i) => (
                <img key={i} src={asset.logo} className="size-4 rounded-full border border-white/10 bg-black shadow-sm" title={`${asset.symbol}: ${asset.amount.toLocaleString(undefined, { maximumFractionDigits: 1 })}`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ))}
              {(!stats?.topAssets || stats.topAssets.length === 0) && (
                <span className="text-[9px] text-white/30 italic">No assets</span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-[9px] text-white/30">—</span>
        )}
      </td>

      {/* 6. PNL COLUMN */}
      <td className="px-3 py-2 text-right align-top">
        {!isBuy && tradeIntel ? (
          <div className="flex flex-col items-end gap-1">
            {tradeIntel.profitSol !== undefined ? (
              <span className={`text-[12px] font-black font-mono ${tradeIntel.profitSol > 0 ? 'text-[#00FFA3]' : tradeIntel.profitSol < 0 ? 'text-[#FF3358]' : 'text-white/50'}`}>
                {tradeIntel.profitSol > 0 ? '+' : ''}{(tradeIntel.profitSol * 100).toFixed(1)}%
              </span>
            ) : (
              <span className="text-[10px] text-white/40 italic">ROI N/A</span>
            )}
            {tradeIntel.holdTimeSeconds !== undefined && (
              <span className="text-[9px] text-white/40 font-mono mt-0.5 bg-white/5 px-1.5 py-0.5 rounded">
                {formatDuration(tradeIntel.holdTimeSeconds)}
              </span>
            )}
          </div>
        ) : (
          <span className="text-[9px] text-white/20">—</span>
        )}
      </td>    </tr>
  );
});
